import { useState } from 'react';
import { api } from '../lib/api.js';

// "Send it to someone" — recipients + your own message, straight after creating a
// link or from the register later. Copying a URL into your own mail client works
// fine, but it loses the branding and the context, and it's one more step at the
// exact moment you're trying to get the thing sent.
//
// The email carries the LINK, never a pre-verified magic link: each recipient
// still confirms their own address at the door, so a forwarded email gets the
// forwardee nowhere and the register stays truthful about who actually read it.
export default function ShareSendPanel({ scope, link, compact = false }) {
  const apiScope = scope === 'my' ? 'my' : 'admin-client';
  const [to, setTo] = useState('');
  const [message, setMessage] = useState('');
  const [state, setState] = useState('idle'); // idle | sending | sent | error
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const restricted = (link.allowedEmails || []).length > 0;

  async function send() {
    setState('sending'); setError('');
    try {
      const r = await api.shareReportSend(apiScope, link.id, { recipients: to, message });
      setResult(r); setState('sent'); setTo('');
    } catch (e) { setError(e.message); setState('error'); }
  }

  if (state === 'sent' && result) {
    return (
      <div style={{ ...wrap, borderColor: 'var(--brand)' }}>
        <div style={{ fontSize: 13.5, fontWeight: 700 }}>Sent to {result.sent} {result.sent === 1 ? 'person' : 'people'}</div>
        <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4, lineHeight: 1.5 }}>
          {(result.recipients || []).join(', ')}
          {result.failed?.length ? ` · couldn't send to ${result.failed.join(', ')}` : ''}
          {result.invited ? ' · they were added to this link’s invite list so it opens for them.' : ''}
        </div>
        <button style={{ ...mini, marginTop: 10 }} onClick={() => { setState('idle'); setResult(null); }}>Send to someone else</button>
      </div>
    );
  }

  return (
    <div style={wrap}>
      {!compact && <div style={{ fontSize: 13.5, fontWeight: 700, marginBottom: 8 }}>Send it by email</div>}
      <label style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 10 }}>
        <span style={label}>To</span>
        <textarea
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="sam@sponsor.co.za, jo@venue.co.za"
          style={{ ...input, minHeight: 54, resize: 'vertical', fontFamily: 'inherit' }}
        />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <span style={label}>Your message (optional)</span>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Here's the live sales report for the festival — it updates itself, so check it any time."
          style={{ ...input, minHeight: 70, resize: 'vertical', fontFamily: 'inherit' }}
        />
      </label>
      <p style={{ fontSize: 11.5, color: 'var(--muted)', margin: '8px 0 0', lineHeight: 1.5 }}>
        They get the link, not a way in — each person still confirms their own email before anything loads.
        {restricted ? ' Anyone you send to is added to this link’s invite list.' : ''}
      </p>
      {error && <div style={{ fontSize: 12.5, color: '#c0392b', marginTop: 8 }}>{error}</div>}
      <button
        style={{ ...primary, marginTop: 10, opacity: state === 'sending' || !to.trim() ? 0.55 : 1 }}
        disabled={state === 'sending' || !to.trim()}
        onClick={send}
      >
        {state === 'sending' ? 'Sending…' : 'Send'}
      </button>
    </div>
  );
}

const wrap = { border: '1px solid var(--border)', borderRadius: 12, padding: 13, background: 'var(--card)' };
const label = { fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' };
const input = { padding: '11px 12px', fontSize: 15, borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', width: '100%' };
const primary = { padding: '11px 18px', minHeight: 44, borderRadius: 10, border: 'none', background: 'var(--brand)', color: '#fff', fontSize: 14.5, fontWeight: 700, cursor: 'pointer' };
const mini = { padding: '7px 11px', minHeight: 36, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--card)', color: 'var(--text)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' };
